define('BayEngageReviews', [
    'BayReviews.ScriptLoader',
    'BayReviews.ProductData',
    'BayEngageReviews.Placeholders'
], function (
    ScriptLoader,
    ProductData,
    Placeholders
) {
    'use strict';

    /**
     * Build the shared payload object used by the widget bundle.
     *
     * WordPress equivalent: class-bayreviews-public.php::build_script_payload()
     */
    function buildBasePayload(config) {
        return {
            bundleSrc: config.scriptUrl,
            commonData: {
                storeDomain: config.storeUrl,
                productId: '',
                productSku: '',
                productName: '',
                userId: '',
                userName: '',
                userEmail: ''
            },
            tbrConfig: {
                platform: 'ns',
                publicKey: config.publicKey || '',
                storeUrl: config.storeUrl,
                storeDomain: config.storeUrl,
                productId: '',
                productName: '',
                productImageUrl: '',
                productUrl: '',
                productStockStatus: '0',
                userId: '',
                userMail: '',
                userName: '',
                orderId: '',
                pageUrl: ''
            }
        };
    }

    function resolveScriptUrl(rawConfig) {
        if (rawConfig.scriptUrl) {
            return rawConfig.scriptUrl;
        }
        var env = rawConfig.environment || 'prod';
        switch (env) {
            case 'local':
            case 'dev':
                return 'https://tbr.dev.bm247.net/d/tbr-bundle.min.js';
            case 'prod':
            default:
                return 'https://sf-rv.bayengage.com/d/tbr-bundle.min.js';
        }
    }

    /**
     * Resolve the effective store URL based on the selected environment.
     * - local / dev -> fixed dev store URL
     * - prod        -> configured storeDomain, falling back to current hostname
     *
     * In production the value is normalized to https:// and trailing slashes are
     * removed so it matches the format used by the dev default and the API.
     */
    function resolveStoreUrl(rawConfig, storeDomain) {
        var env = rawConfig.environment || 'prod';
        if (env === 'local' || env === 'dev') {
            return 'https://testbe-dev2-review-nov25.mybigcommerce.com';
        }

        var url = storeDomain || (window.location && window.location.hostname) || '';
        if (!url) {
            return url;
        }

        url = url.replace(/\/$/, '');
        if (!/^https?:\/\//i.test(url)) {
            url = 'https://' + url;
        }
        return url;
    }

    /**
     * Resolve the Beautifyr public API base URL used for PDP / PLP review statistics.
     */
    function resolveBeautifyrApiUrl(rawConfig) {
        if (rawConfig.beautifyrApiUrl) {
            return rawConfig.beautifyrApiUrl;
        }
        var env = rawConfig.environment || 'prod';
        switch (env) {
            case 'local':
            case 'dev':
                return 'https://api-cache.dev.bm247.net';
            case 'prod':
            default:
                return 'https://api-rvsf.bayengage.com';
        }
    }

    /**
     * Resolve the review badge store id.
     */
    function resolveBadgeStoreId(rawConfig) {
        if (rawConfig.badgeStoreId) {
            return rawConfig.badgeStoreId;
        }
        var env = rawConfig.environment || 'prod';
        switch (env) {
            case 'local':
            case 'dev':
                return '5c2e44adf496';
            case 'prod':
            default:
                return '1b9d2cb17154';
        }
    }

    /**
     * Resolve the review badge image URL based on environment and store id.
     */
    function resolveBadgeImageUrl(rawConfig) {
        var storeId = resolveBadgeStoreId(rawConfig);
        if (!storeId) {
            return '';
        }
        var env = rawConfig.environment || 'prod';
        switch (env) {
            case 'local':
            case 'dev':
                return 'https://img-rv.dev.bm247.net/public/' + storeId + '/reviews/badges/current_badge.png';
            case 'prod':
            default:
                return 'https://img-rv.bayengage.com/public/' + storeId + '/reviews/badges/current_badge.png';
        }
    }

    return {
        mountToApp: function (container) {
            var env = container.getComponent('Environment');
            var rawConfig = (env ? env.getConfig('bayEngageReviews') : null) || {};

            var configuredStoreDomain = rawConfig.storeDomain || (window.location && window.location.hostname) || '';
            var config = {
                environment: rawConfig.environment || 'prod',
                scriptUrl: resolveScriptUrl(rawConfig),
                storeDomain: configuredStoreDomain,
                storeUrl: resolveStoreUrl(rawConfig, configuredStoreDomain),
                beautifyrApiUrl: resolveBeautifyrApiUrl(rawConfig),
                containerId: rawConfig.containerId || 'bayengage-reviews-widget',
                publicKey: rawConfig.publicKey || '',
                badgeImageUrl: resolveBadgeImageUrl(rawConfig),
                debugMode: !!rawConfig.debugMode
            };

            var payload = buildBasePayload(config);

            // Resolve user profile asynchronously. When it resolves, merge the
            // user data into the payload and update the widget globals without
            // re-injecting the bundle.
            var userProfileComponent = container.getComponent('UserProfile');
            if (userProfileComponent && typeof userProfileComponent.getUserProfile === 'function') {
                userProfileComponent.getUserProfile().then(function (profile) {
                    if (profile && profile.isloggedin) {
                        var userName = ((profile.firstname || '') + ' ' + (profile.lastname || '')).trim();
                        var userId = String(profile.internalid || '');
                        var userEmail = profile.email || '';

                        payload.commonData.userId = userId;
                        payload.commonData.userName = userName;
                        payload.commonData.userEmail = userEmail;

                        payload.tbrConfig.userId = userId;
                        payload.tbrConfig.userName = userName;
                        payload.tbrConfig.userMail = userEmail;

                        ScriptLoader.updateGlobals(payload);
                    }
                }).catch(function (err) {
                    if (config.debugMode && window.console) {
                        window.console.warn('BayReviews: UserProfile resolution failed', err);
                    }
                });
            }

            var pdp = container.getComponent('PDP');
            var plp = container.getComponent('PLP');

            // Delegate all placeholder insertion to the config-driven module.
            Placeholders.mount(config, payload, pdp, plp);

            // Keep the global payload in sync when a matrix variant is selected.
            if (pdp && typeof pdp.on === 'function') {
                pdp.on('afterOptionSelection', function () {
                    var productData = ProductData.getPdpProductData(pdp, config);
                    if (!productData) {
                        return;
                    }
                    ProductData.applyProductData(payload, productData);
                    ScriptLoader.updateGlobals(payload);
                });
            }

            // Kick off the shared loader. The placeholders module also calls
            // load() whenever it inserts placeholders; the loader coalesces
            // these requests into one injection per page view.
            ScriptLoader.load(payload, {
                debugMode: config.debugMode,
                containerId: config.containerId
            });
        }
    };
});
